import { Amount } from '@ui/components/Amount/Amount';
import { AmountChange } from '@ui/components/Amount/AmountChange';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { LongShortBadge } from '@ui/components/LongShortBadge';
import { PairSideCell, SidePill } from '../shared/PairSideCell';
import { TextTooltip } from '@ui/components/TextTooltip';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { getFirstNonZeroDecimalPlace } from '@ui/lib/bignumber/getFirstNonZeroDecimalPlace';
import { Button } from '@ui/lib/park/ui/button';
import { Link } from '@ui/lib/park/ui/link';
import { Text } from '@ui/lib/park/ui/text';
import {
  ClosePositionDetailExecutionTypeEnum,
  TradeHistoryResponseOperationTypeEnum,
  TradeHistoryResponseTradeTypeEnum,
} from '@ui/services/backend/generated';
import {
  ClosePosition,
  ExecuteCloseSuccessful,
  ExecuteDecreaseOrderSuccessful,
  PositionDecreased,
  TradeHistory,
  UserCloseTradeSuccessful,
} from '@ui/services/backend/hooks/useTradeHistory/type';
import { getHasInvalidLeverage } from '@ui/services/backend/hooks/useTradeHistory/util';
import { Nullable } from '@ui/types/utility';
import { formatDate } from '@ui/utils/formatDate';
import { getTokenPriceDisplayDecimals } from '@ui/utils/getTokenPriceDisplayDecimals';
import { getTokenSizeDisplayDecimals } from '@ui/utils/getTokenSizeDisplayDecimals';
import { format } from 'date-fns';
import { omit } from 'lodash-es';
import { TFunction, useTranslation } from 'next-i18next';
import { ReactNode, useMemo } from 'react';
import {
  Box,
  Grid,
  HStack,
  Stack,
  styled,
  VStack,
  VstackProps,
} from 'styled-system/jsx';

import { useTradePairInfosByPairBase } from '../../../../hooks/trade/useTradePairInfos';
import { useGetExecutionFeeUsd } from '../../hooks/useGetExecutionFee';
import { useIsTradePageContainerDesktop } from '../../hooks/useIsTradePageContainerDesktop';
import { LeverageBadge } from '../LeverageBadge';
import { getSidePillProps, valueStyle } from '../PositionList/styles';
import { Row } from '../Row';
import { getGroupHashColor, shortenGroupHash } from './getGroupHashColor';
import {
  HISTORY_GROUP_ATTR,
  HISTORY_SCOPE_ATTR,
  HISTORY_STRIPE_CLASS,
  useHistoryCardContext,
  useHistoryHover,
} from './historyCardContext';
import { HistoryClosePrice } from './HistoryClosePrice';
import {
  getShouldHideHistoryCollateral,
  HistoryCollateral,
} from './HistoryCollateral';
import { HistoryShareOverlay } from './HistoryShareOverlay';
import { HistoryStatusBadge } from './HistoryStatusBadge';

const ADL_LEARN_MORE_URL =
  'https://leverup.gitbook.io/docs/trading/trading-rwa#market-close-force-close';

/**
 * Thin colored bar on the left edge of a row, keyed off the event's groupHash
 * (positionHash). Association is conveyed through this stripe rather than a
 * heavy row fill: on hover the active group's stripes stay lit while every
 * other group's stripe dims back (driven by injected CSS — see
 * HistoryHoverProvider), so the focused position "lights up" as a column.
 * Ungrouped events (e.g. limit-order lifecycle) get a neutral grey.
 */
const GroupStripe = ({ hash }: { hash: Nullable<string> }) => (
  <Box
    className={HISTORY_STRIPE_CLASS}
    position="absolute"
    left={0}
    top={0}
    bottom={0}
    width="1px"
    opacity={hash ? 0.85 : 0.3}
    transition="width 120ms ease, opacity 120ms ease"
    // dynamic color must be inline; Panda only emits CSS for static values
    style={{ backgroundColor: getGroupHashColor(hash) }}
  />
);

/**
 * Small "#a3f9" label identifying the event's group. Purely informational — not
 * interactive (filtering would be misleading under paginated lazy loading). The
 * color cue lives on the left stripe, so no dot here.
 */
const GroupHashBadge = ({ hash }: { hash: Nullable<string> }) => (
  <Text
    textStyle="numbers.micro"
    color="v2.text.subtle"
    data-cy="HistoryGroupHash"
  >
    {shortenGroupHash(hash)}
  </Text>
);

/**
 * Grid shell carrying the group's data attributes + hover handlers. Hover
 * highlighting is driven purely by injected CSS (see HistoryHoverProvider), so
 * this never re-renders on hover — it just reports the hovered group via a
 * stable imperative setter. Heavy row content is passed as children and is
 * never re-rendered by hovering.
 */
const HistoryRowShell = ({
  groupHash,
  children,
}: {
  groupHash: Nullable<string>;
  children: ReactNode;
}) => {
  const { scopeId, setHoveredGroup } = useHistoryHover();

  return (
    <Grid
      position="relative"
      gridTemplateColumns="minmax(60px, 6fr) minmax(100px, 10fr) minmax(110px, 10fr) minmax(140px, 11fr) minmax(80px, 9fr) minmax(150px, 14fr) minmax(120px, 11fr)"
      columnGap={0}
      gap={0}
      rowGap={0}
      px={4}
      py={2}
      alignItems="center"
      borderBottom="1px solid"
      borderBottomColor="v2.border.hairline"
      transition="background 120ms ease"
      // #12: the row under the cursor always lights up (grouped or not);
      // group-mates get the softer injected surface1 tint
      _hover={{ backgroundColor: 'rgba(255, 255, 255, 0.035)!' }}
      {...{
        [HISTORY_SCOPE_ATTR]: scopeId,
        [HISTORY_GROUP_ATTR]: groupHash ?? '',
      }}
      onMouseEnter={() => setHoveredGroup(groupHash)}
      onMouseLeave={() => setHoveredGroup(null)}
    >
      <GroupStripe hash={groupHash} />
      {children}
    </Grid>
  );
};

export const HistoryCard = ({ history }: { history: TradeHistory }) => {
  const { value: isContainerDesktop } = useIsTradePageContainerDesktop();
  const { t } = useTranslation();

  if (!isContainerDesktop) {
    return <HistoryCardMobile history={history} />;
  }

  return (
    <HistoryRowShell groupHash={history.groupHash}>
      <VStack gap={1} alignItems="flex-start">
        <Text
          textStyle="numbers.xsmall"
          color="v2.text.secondary"
          data-cy="HistoryTimestamp"
          data-cy-value={Math.floor(
            new Date(history.blockTime).getTime() / 1000,
          )}
        >
          <Link href={history.explorer} target="_blank">
            {format(history.blockTime, 'dd MMM')}
            <br />
            {format(history.blockTime, 'HH:mm:ss')}
          </Link>
        </Text>
        <GroupHashBadge hash={history.groupHash} />
      </VStack>

      <PairSideCell
        image={history.pair.image}
        name={history.pair.baseName}
        pill={<HistorySidePill history={history} />}
      />

      <PositionSize history={history} />

      <HistoryCollateral history={history} />

      <HistoryTypeLabel history={history} t={t} />

      <HistoryClosePrice history={history} />

      {history.type ===
        TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful ||
      history.type ===
        TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful ||
      history.type === TradeHistoryResponseOperationTypeEnum.ClosePosition ||
      history.type ===
        TradeHistoryResponseOperationTypeEnum.PositionDecreased ||
      history.type ===
        TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful ? (
        <HStack gap={2} justifyContent="flex-start" alignItems="center">
          <RealizedPnl history={history} />
          <HistoryShareOverlay history={history} />
        </HStack>
      ) : (
        <Box />
      )}
    </HistoryRowShell>
  );
};

const HistoryCardMobile = ({ history }: { history: TradeHistory }) => {
  const { t } = useTranslation();

  return (
    <Box
      position="relative"
      overflow="hidden"
      display={{ base: 'block' }}
      w="full"
      px="3"
      py="3"
      border="1px solid"
      borderColor="v2.border.default"
      bg="v2.bg.surface1"
      borderRadius="4px"
      mb="2"
    >
      <GroupStripe hash={history.groupHash} />
      <HStack justifyContent="space-between" alignItems="center" mb="2">
        <HStack gap="2" alignItems="center">
          <styled.img src={history.pair.image} boxSize="6" borderRadius="6px" />
          <Text textStyle="v2.ui" color="v2.text.primary">
            {history.pair.baseName}
          </Text>
          <LongShortBadge
            ml={1}
            isLong={history.isLong}
            {...getSidePillProps(history.isLong)}
          />
          <Leverage history={history} />
          <GroupHashBadge hash={history.groupHash} />
        </HStack>

        {history.type ===
        TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful ? (
          <RealizedPnl history={history} />
        ) : history.type ===
          TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful ? (
          <RealizedPnl history={history} />
        ) : history.type ===
          TradeHistoryResponseOperationTypeEnum.ClosePosition ? (
          <RealizedPnl history={history} />
        ) : history.type ===
          TradeHistoryResponseOperationTypeEnum.PositionDecreased ? (
          <RealizedPnl history={history} />
        ) : history.type ===
          TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful ? (
          <RealizedPnl history={history} />
        ) : (
          <Box />
        )}
      </HStack>

      <Box borderTop="1px solid" borderTopColor="v2.border.hairline">
        <Row label={t('common.Action')}>
          <HistoryTypeLabel history={history} t={t} />
        </Row>

        <Row label={t('common.Detail')}>
          <PositionSize history={history} />
        </Row>

        {!getShouldHideHistoryCollateral(history) && (
          <Row label={t('common.Collateral')}>
            <HistoryCollateral history={history} />
          </Row>
        )}

        {(history.type ===
          TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful ||
          history.type ===
            TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful ||
          history.type ===
            TradeHistoryResponseOperationTypeEnum.ClosePosition ||
          history.type ===
            TradeHistoryResponseOperationTypeEnum.PositionDecreased ||
          history.type ===
            TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful) && (
          <Row label={t('common.Close Price')}>
            <AmountUsd
              css={valueStyle}
              value={history.detail.closePrice}
              opts={{
                displayDecimals: getTokenPriceDisplayDecimals(
                  history.detail.closePrice.toNumber(),
                  history.pair.priceDisplayDecimals,
                ),
              }}
              data-cy="HistoryClosePrice-Mobile"
              data-cy-value={history.detail.closePrice}
            />
          </Row>
        )}

        <Row label={t('common.Time')}>
          <Text
            css={valueStyle}
            data-cy="HistoryTimestamp"
            data-cy-value={Math.floor(
              new Date(history.blockTime).getTime() / 1000,
            )}
          >
            <Link href={history.explorer} target="_blank">
              {format(history.blockTime, 'dd MMM')}
              <br />
              {format(history.blockTime, 'HH:mm')}
            </Link>
          </Text>
        </Row>
      </Box>
      <HStack mt="3" justify="flex-end">
        {history.type ===
          TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful ||
        history.type ===
          TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful ||
        history.type === TradeHistoryResponseOperationTypeEnum.ClosePosition ||
        history.type ===
          TradeHistoryResponseOperationTypeEnum.PositionDecreased ||
        history.type ===
          TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful ? (
          <HistoryShareOverlay history={history}>
            <Button variant="secondary" w="25%">
              {t('common.Share')}
            </Button>
          </HistoryShareOverlay>
        ) : null}
      </HStack>
    </Box>
  );
};

const RealizedPnl = ({
  history,
}: {
  history:
    | UserCloseTradeSuccessful
    | ExecuteCloseSuccessful
    | ExecuteDecreaseOrderSuccessful
    | ClosePosition
    | PositionDecreased;
}) => {
  const { t } = useTranslation();
  const latestCollateralPrice = usePayTokenPriceLatest(history.lvToken);
  const collateralPrice = history.tokenInPrice ?? latestCollateralPrice;

  const pnlUsdAfterFee = useMemo(() => {
    return history.detail.getPnlAfterFeeUsd(collateralPrice);
  }, [history.detail, collateralPrice]);

  const tooltipContent = useMemo(() => {
    const pnlGrossUsd = history.detail.getPnlUsd(collateralPrice);
    const closeFeeUsd = collateralPrice
      ? history.detail.closeFee.multipliedBy(collateralPrice)
      : undefined;
    const fundingFee = history.detail.fundingFee.plus(
      history.detail.holdingFee,
    );
    const fundingFeeUsd = collateralPrice
      ? fundingFee.multipliedBy(collateralPrice).negated()
      : undefined;

    return (
      <Stack minW="60">
        <Box>
          <HStack
            justify="space-between"
            borderBottom="1px solid"
            borderBottomColor="greyWhite.outline"
            pb={2}
            textStyle="headings.small"
          >
            <Text color="greyWhite.white">
              {t('common.Realized PnL(after fees)')}
            </Text>

            <AmountChange
              value={pnlUsdAfterFee}
              hasColor
              opts={{
                prefix: '$',
                isShowSign: false,
              }}
              data-cy="RealizedPnlInUsd"
              data-cy-value={pnlUsdAfterFee}
            />
          </HStack>
        </Box>
        <Stack gap={1}>
          <HStack justifyContent="space-between" w="full">
            <Text css={valueStyle}>{t('common.PNL')}</Text>
            <AmountChange
              value={pnlGrossUsd}
              hasColor
              css={omit(valueStyle, 'color')}
              opts={{ prefix: '$' }}
            />
          </HStack>
          <HStack justifyContent="space-between" w="full">
            <Text css={valueStyle}>{t('common.Close Fee')}</Text>
            {history.detail.closeFee.isZero() ? (
              <Text css={{ ...valueStyle, color: 'system.successLight' }}>
                {t('common.Free')}
              </Text>
            ) : (
              <AmountUsd
                value={closeFeeUsd}
                css={valueStyle}
                opts={{ prefix: '$' }}
              />
            )}
          </HStack>
          <HStack justifyContent="space-between" w="full">
            <Text css={valueStyle}>{t('common.Funding Fee')}</Text>
            {fundingFee.isZero() ? (
              <Text css={{ ...valueStyle, color: 'system.successLight' }}>
                {t('common.Free')}
              </Text>
            ) : (
              <AmountChange
                value={fundingFeeUsd}
                hasColor
                css={omit(valueStyle, 'color')}
                opts={{ prefix: '$' }}
              />
            )}
          </HStack>
        </Stack>
      </Stack>
    );
  }, [history.detail, collateralPrice, t]);

  return (
    <HStack gap={0}>
      {/* <Text textStyle="text.xsmall" color="greyWhite.lightGrey1" mr={1}>
        {t('common.Realized PnL(after fees)')}:
      </Text> */}
      <VStack gap={1} alignItems="flex-start" cursor="help">
        <AmountChange
          value={pnlUsdAfterFee}
          hasColor
          textStyle="numbers.small"
          opts={{
            prefix: '$',
            isShowSign: false,
          }}
          data-cy="RealizedPnlInUsd"
          data-cy-value={pnlUsdAfterFee}
        />

        <TextTooltip content={tooltipContent}>
          <AmountChange
            value={history.detail.pnlAfterFee}
            hasColor
            before="("
            after=")"
            textStyle="numbers.xsmall"
            opts={{
              suffix: ' ' + history.lvToken.displaySymbol,
              displayDecimals:
                getFirstNonZeroDecimalPlace(
                  history.detail.pnlAfterFee.toString(),
                ) ?? 2,
            }}
            data-cy="RealizedPnlInToken"
            data-cy-value={history.detail.pnl}
          />
        </TextTooltip>
      </VStack>
    </HStack>
  );
};
const useHistoryPairPrice = (
  history: TradeHistory,
  latestPairPrice: ReturnType<typeof usePriceLatest>,
) =>
  useMemo(() => {
    switch (history.type) {
      case TradeHistoryResponseOperationTypeEnum.OpenLimitOrder:
        return history.detail.price;
      case TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated:
        return (
          history.detail.triggerPrice ?? history.entryPrice ?? latestPairPrice
        );
      case TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated:
        return (
          history.detail.triggerPrice ??
          history.detail.oldTriggerPrice ??
          history.entryPrice ??
          latestPairPrice
        );
      case TradeHistoryResponseOperationTypeEnum.OpenMarketTrade:
      case TradeHistoryResponseOperationTypeEnum.OpenPosition:
      case TradeHistoryResponseOperationTypeEnum.PositionIncreased:
        return history.detail.atPrice;
      case TradeHistoryResponseOperationTypeEnum.MarketPendingTrade:
        return history.detail.price;
      case TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful:
      case TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful:
      case TradeHistoryResponseOperationTypeEnum.ClosePosition:
      case TradeHistoryResponseOperationTypeEnum.PositionDecreased:
      case TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful:
        return history.detail.closePrice;
      default:
        return history.entryPrice ?? latestPairPrice;
    }
  }, [history, latestPairPrice]);

const Leverage = ({ history }: { history: TradeHistory }) => {
  const hasInvalidLeverage = getHasInvalidLeverage(history);
  const latestPairPrice = usePriceLatest(history.pair.baseSymbol);
  const latestCollateralPrice = usePayTokenPriceLatest(history.tokenIn);
  const collateralPrice = history.tokenInPrice ?? latestCollateralPrice;
  const pairPrice = useHistoryPairPrice(history, latestPairPrice);

  const executionFeeUsd = useGetExecutionFeeUsd();
  const pairInfo = useTradePairInfosByPairBase(history.pair.address);

  const leverage = useMemo(() => {
    return history.getLeverage({
      collateralPrice,
      // FIXME: should use execution fee from history, refine in post mvp
      executionFeeUsd,
      // FIXME: should use openFeeP or openFee from history, refine in post mvp
      openFeeP: pairInfo?.feeConfig?.openFeeP,
      lvTokenDiscountP: pairInfo?.feeConfig?.lvTokenDiscountP,
      pairPrice,
    });
  }, [
    collateralPrice,
    executionFeeUsd,
    history,
    pairInfo?.feeConfig?.openFeeP,
    pairInfo?.feeConfig?.lvTokenDiscountP,
    pairPrice,
  ]);

  if (hasInvalidLeverage) {
    return;
  }

  return (
    <LeverageBadge leverage={leverage} isDegen={history.pair.isDegenPair} />
  );
};

/** unified side+leverage pill (#28) reusing Leverage's derivation */
const HistorySidePill = ({ history }: { history: TradeHistory }) => {
  const hasInvalidLeverage = getHasInvalidLeverage(history);
  const latestPairPrice = usePriceLatest(history.pair.baseSymbol);
  const latestCollateralPrice = usePayTokenPriceLatest(history.tokenIn);
  const collateralPrice = history.tokenInPrice ?? latestCollateralPrice;
  const executionFeeUsd = useGetExecutionFeeUsd();
  const pairInfo = useTradePairInfosByPairBase(history.pair.address);
  const pairPrice = useHistoryPairPrice(history, latestPairPrice);

  const leverage = useMemo(() => {
    if (hasInvalidLeverage) return undefined;

    return history.getLeverage({
      collateralPrice,
      executionFeeUsd,
      openFeeP: pairInfo?.feeConfig?.openFeeP,
      lvTokenDiscountP: pairInfo?.feeConfig?.lvTokenDiscountP,
      pairPrice,
    });
  }, [
    hasInvalidLeverage,
    collateralPrice,
    executionFeeUsd,
    history,
    pairInfo?.feeConfig?.openFeeP,
    pairInfo?.feeConfig?.lvTokenDiscountP,
    pairPrice,
  ]);

  if (leverage === undefined || leverage === null) {
    return (
      <SidePill isLong={history.isLong}>
        {history.isLong ? 'LONG' : 'SHORT'}
      </SidePill>
    );
  }

  return <SidePill isLong={history.isLong} leverage={leverage} />;
};
const PositionSize = ({ history }: { history: TradeHistory }) => {
  const latestPairPrice = usePriceLatest(history.pair.baseSymbol);

  const pairPrice = useMemo(() => {
    switch (history.type) {
      case TradeHistoryResponseOperationTypeEnum.OpenLimitOrder:
        return history.detail.price;
      case TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated:
        return (
          history.detail.triggerPrice ?? history.entryPrice ?? latestPairPrice
        );
      case TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated:
        return (
          history.detail.triggerPrice ??
          history.detail.oldTriggerPrice ??
          history.entryPrice ??
          latestPairPrice
        );
      case TradeHistoryResponseOperationTypeEnum.OpenMarketTrade:
      case TradeHistoryResponseOperationTypeEnum.OpenPosition:
      case TradeHistoryResponseOperationTypeEnum.PositionIncreased:
        return history.detail.atPrice;
      case TradeHistoryResponseOperationTypeEnum.MarketPendingTrade:
        return history.detail.price;
      case TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful:
      case TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful:
        return history.detail.closePrice;
      case TradeHistoryResponseOperationTypeEnum.ClosePosition:
        return history.detail.closePrice;
      case TradeHistoryResponseOperationTypeEnum.PositionDecreased:
        return history.detail.closePrice;
      case TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful:
        return history.detail.closePrice;
      default:
        return history.entryPrice ?? latestPairPrice;
    }
  }, [history, latestPairPrice]);

  const sizeDisplayDecimals = useMemo(() => {
    const p = pairPrice?.toNumber?.();
    if (p === undefined || Number.isNaN(p)) return 2;

    return getTokenSizeDisplayDecimals(p);
  }, [pairPrice]);

  return (
    <VStack gap={0} alignItems="flex-start">
      <AmountUsd
        value={history.getSizeUsd(pairPrice)}
        textStyle="numbers.small"
        color="v2.text.secondary"
        opts={{
          prefix: '$',
        }}
        data-cy="PositionSizeInUsd"
        data-cy-value={history.getSizeUsd(pairPrice)}
      />
      <Amount
        value={history.size}
        textStyle="numbers.xsmall"
        color="v2.text.muted"
        opts={{
          suffix: ' ' + history.pair.baseSymbol + ')',
          displayDecimals: sizeDisplayDecimals,
          prefix: '(',
        }}
        data-cy="PositionSizeInToken"
        data-cy-value={history.size}
      />
    </VStack>
  );
};

const HistoryTypeLabel = ({
  history,
  t,
}: {
  history: TradeHistory;
  t: TFunction;
}) => {
  const isAdl =
    (history.type ===
      TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful ||
      history.type ===
        TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful) &&
    history.detail.executionType === ClosePositionDetailExecutionTypeEnum.Adl;

  return (
    <HStack gap={1} alignItems="center" justifyContent="flex-start">
      <Text
        textStyle="v2.label"
        color="v2.text.secondary"
        data-cy="HistoryType"
      >
        {getHistoryTypeLabel(history, t)}
      </Text>
      {isAdl && (
        <Link
          href={ADL_LEARN_MORE_URL}
          target="_blank"
          rel="noreferrer"
          aria-label="Why was this position force-closed?"
          title="Why was this position force-closed?"
          textDecoration="none"
        >
          <Text
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            w="4"
            h="4"
            borderRadius="50%"
            border="1px solid"
            borderColor="v2.text.subtle"
            color="v2.text.muted"
            textStyle="text.xsmall"
            cursor="pointer"
          >
            ?
          </Text>
        </Link>
      )}
    </HStack>
  );
};

const getHistoryTypeLabel = (history: TradeHistory, t: TFunction) => {
  switch (history.type) {
    case TradeHistoryResponseOperationTypeEnum.OpenLimitOrder:
      return t('common.Place Limit');
    case TradeHistoryResponseOperationTypeEnum.MarketPendingTrade:
      return t('common.Open Pending');
    case TradeHistoryResponseOperationTypeEnum.PendingTradeRefund:
      return t('common.Refunded (Market)');
    case TradeHistoryResponseOperationTypeEnum.LimitOrderRefund:
      return t('common.Refunded (Limit)');
    case TradeHistoryResponseOperationTypeEnum.OpenMarketTrade:
      return history.tradeType === TradeHistoryResponseTradeTypeEnum.Position
        ? t('common.Open (Market)')
        : t('common.Open (Limit Fill)');
    case TradeHistoryResponseOperationTypeEnum.OpenPosition:
      return t('common.Open Position');
    case TradeHistoryResponseOperationTypeEnum.PositionIncreased:
      return t('common.Increase Position');
    case TradeHistoryResponseOperationTypeEnum.CancelLimitOrder:
      return t('common.Cancel Limit');
    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated:
      return `Create ${history.detail.kind}`;
    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderCancelled:
      return `Cancel ${history.detail.kind}`;
    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated:
      return `Update ${history.detail.kind}`;
    case TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful:
    case TradeHistoryResponseOperationTypeEnum.ClosePosition:
      return t('common.Close');
    case TradeHistoryResponseOperationTypeEnum.PositionDecreased:
      return t('common.Decrease Position');
    case TradeHistoryResponseOperationTypeEnum.UpdateMargin:
      return history.detail.nextCollateral.gt(history.detail.previousCollateral)
        ? t('common.Add Margin')
        : t('common.Remove Margin');
    case TradeHistoryResponseOperationTypeEnum.UpdateOrderSl:
    case TradeHistoryResponseOperationTypeEnum.UpdateTradeSl:
      return t('common.Update SL');
    case TradeHistoryResponseOperationTypeEnum.UpdateOrderTp:
    case TradeHistoryResponseOperationTypeEnum.UpdateTradeTp:
      return t('common.Update TP');
    case TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful:
    case TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful:
      if (
        history.detail.executionType ===
        ClosePositionDetailExecutionTypeEnum.Liq
      )
        return t('common.Liquidated');
      if (
        history.detail.executionType === ClosePositionDetailExecutionTypeEnum.Tp
      )
        return t('common.Close (TP)');
      if (
        history.detail.executionType === ClosePositionDetailExecutionTypeEnum.Sl
      )
        return t('common.Close (SL)');
      if (
        history.detail.executionType ===
        ClosePositionDetailExecutionTypeEnum.Adl
      )
        return t('common.Close (ADL)');
    case TradeHistoryResponseOperationTypeEnum.ExecuteCloseRejected:
    case TradeHistoryResponseOperationTypeEnum.ExecuteLimitOrderRejected:
    case TradeHistoryResponseOperationTypeEnum.ExecuteLimitOrderSuccessful:
      // these event should be filtered out
      return '';
  }
};
